import { useState, useEffect } from 'react';
import { adminGetAdmins, adminCreateAdmin } from '../../lib/adminApi';
import AdminSidebar from '../../components/AdminSidebar';

const NAVY = '#0a2540'; const ORANGE = '#F37428'; const BLUE = '#0176CE';

function fmtDate(raw) { if (!raw) return '—'; return new Date(raw).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }

const EMPTY = { full_name: '', email: '', password: '', role: 'admin' };

export default function AdminUsers() {
  const [admins, setAdmins]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved]         = useState(false);

  function load() {
    setLoading(true);
    adminGetAdmins()
      .then(res => { setAdmins(res.admins || res || []); setLoading(false); })
      .catch(e  => { setError(e.message); setLoading(false); });
  }

  useEffect(load, []);

  async function handleCreate(e) {
    e.preventDefault();
    setSaveError(''); setSaving(true);
    try {
      await adminCreateAdmin(form);
      setShowForm(false);
      setForm(EMPTY);
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
      load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={s.page}>
      <AdminSidebar active="users" />
      <main style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Admin Users</h1>
            <p style={s.sub}>Superadmin access only — manage console users</p>
          </div>
          <button style={s.addBtn} onClick={() => { setShowForm(true); setForm(EMPTY); setSaveError(''); }}>+ Add Admin</button>
        </div>

        {saved && (
          <div style={{ background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.3)', borderRadius: 8, color: '#059669', fontSize: 13, padding: '10px 16px', marginBottom: 20 }}>
            Admin user created successfully. They'll receive a welcome email with setup instructions.
          </div>
        )}

        {loading && <div style={{ color: '#64748b', padding: 32 }}>Loading…</div>}
        {error   && <div style={{ color: '#ef4444', padding: 32 }}>{error}</div>}

        {!loading && !error && (
          <div style={s.card}>
            <table style={s.table}>
              <thead>
                <tr>{['Name', 'Email', 'Role', '2FA', 'Last Login', 'Created'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {admins.map(a => (
                  <tr key={a.admin_id} style={s.tr}>
                    <td style={{ ...s.td, fontWeight: 600, color: NAVY }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: ORANGE, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
                          {a.full_name?.[0]?.toUpperCase() || 'A'}
                        </div>
                        {a.full_name || '—'}
                      </div>
                    </td>
                    <td style={{ ...s.td, color: '#64748b', fontSize: 12 }}>{a.email}</td>
                    <td style={{ ...s.td }}>
                      <span style={{ background: a.role === 'superadmin' ? 'rgba(243,116,40,0.1)' : 'rgba(1,118,206,0.08)', color: a.role === 'superadmin' ? ORANGE : BLUE, borderRadius: 5, fontSize: 10, fontWeight: 600, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {a.role}
                      </span>
                    </td>
                    <td style={{ ...s.td, textAlign: 'center' }}>
                      {a.totp_enabled ? <span style={{ color: '#059669', fontSize: 12 }}>✓ On</span> : <span style={{ color: '#94a3b8', fontSize: 12 }}>Off</span>}
                    </td>
                    <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{fmtDate(a.last_login_utc)}</td>
                    <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{fmtDate(a.created_on_utc)}</td>
                  </tr>
                ))}
                {admins.length === 0 && (
                  <tr><td colSpan={6} style={{ ...s.td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>No admin users found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {showForm && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <h2 style={s.modalTitle}>Add Admin User</h2>
            {saveError && <div style={s.errorBox}>{saveError}</div>}
            <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={s.label}>Full Name *</label>
                <input style={s.input} placeholder="Jane Smith" value={form.full_name}
                  onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} required autoFocus />
              </div>
              <div>
                <label style={s.label}>Email *</label>
                <input style={s.input} type="email" placeholder="jane@masterfileai.com" value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
              </div>
              <div>
                <label style={s.label}>Temporary Password *</label>
                <input style={s.input} type="password" placeholder="Min 8 characters" value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required minLength={8} />
              </div>
              <div>
                <label style={s.label}>Role *</label>
                <select style={s.input} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button type="submit" style={s.btnPrimary} disabled={saving}>{saving ? 'Creating…' : 'Create Admin'}</button>
                <button type="button" style={s.btnGhost} onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page:      { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main:      { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  header:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 },
  title:     { color: NAVY, fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: '-0.4px' },
  sub:       { color: '#64748b', fontSize: 13, margin: '4px 0 0' },
  addBtn:    { background: ORANGE, border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '10px 18px' },
  card:      { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  table:     { width: '100%', borderCollapse: 'collapse' },
  th:        { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 16px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  tr:        { borderBottom: '1px solid #f1f5f9' },
  td:        { color: '#374151', fontSize: 13, padding: '12px 16px', verticalAlign: 'middle' },
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:     { background: '#fff', borderRadius: 16, padding: '36px 32px', width: '100%', maxWidth: 440, boxShadow: '0 24px 60px rgba(0,0,0,0.25)' },
  modalTitle:{ color: NAVY, fontSize: 18, fontWeight: 700, margin: '0 0 20px' },
  label:     { display: 'block', color: '#64748b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 },
  input:     { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, color: NAVY, fontSize: 14, padding: '10px 12px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  errorBox:  { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#dc2626', fontSize: 13, padding: '10px 14px', marginBottom: 16 },
  btnPrimary:{ background: ORANGE, border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, padding: '11px 20px' },
  btnGhost:  { background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 8, color: '#64748b', cursor: 'pointer', fontSize: 14, fontWeight: 500, padding: '11px 20px' },
};
